import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import Drawer from "@mui/material/Drawer"
import IconButton from "@mui/material/IconButton"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"

import Column from "../../../../../common/components/Column"
import Row from "../../../../../common/components/Row"
import { useIsMobile } from "../../../../../common/utils"
import DialogHeader from "../../../components/DialogHeader"
import CloseUnsavedTileEditorWarningDialog, {
  CLOSE_UNSAVED_TILE_RESPONSES,
} from "../CloseUnsavedTileEditorWarningDialog"
import { useNodeUtils } from "../utils"

export default function EditorDrawer({
  open,
  onClose,
  node,
  editorState,
  tileChanged,
  onChangesSaved,
  onChangesDiscarded,
  title,
  children,
}) {
  const [label, setLabel] = useState("")
  const [isEditingLabel, setIsEditingLabel] = useState(false)
  const [unsavedTileDialogOpen, setUnsavedTileDialogOpen] = useState(false)
  const { updateNodeData, updateNodeEditorData } = useNodeUtils()
  const isMobile = useIsMobile()
  const Container = isMobile ? Dialog : Drawer

  function handleUnsavedTileDialogClosed(response) {
    switch (response) {
      case CLOSE_UNSAVED_TILE_RESPONSES.cancel:
        break
      case CLOSE_UNSAVED_TILE_RESPONSES.discard:
        onChangesDiscarded()
        onClose()
        break
      case CLOSE_UNSAVED_TILE_RESPONSES.save:
        onChangesSaved()
        onClose()
        break
      default:
        break
    }
    setUnsavedTileDialogOpen(false)
  }

  function handleDrawerClicked() {
    if (isEditingLabel) {
      setIsEditingLabel(false)
      updateNodeData(node, { label })
    }
  }

  function handleChangesSaved() {
    onChangesSaved()
    updateNodeEditorData(node, editorState)
    onClose()
  }

  function handleClosed() {
    if (tileChanged) {
      setUnsavedTileDialogOpen(true)
    } else {
      onClose()
    }
  }

  useEffect(() => {
    if (!node) {
      return
    }
    setLabel(node.data.label)
  }, [node])

  return (
    <Container
      anchor="right"
      open={open}
      onClose={handleClosed}
      onClick={handleDrawerClicked}
      scroll="paper"
    >
      <Column>
        <DialogHeader title={title} onClose={handleClosed} />
        <Row sx={{ alignItems: "center", mt: 2, px: { xs: 2, sm: 3 } }}>
          {isEditingLabel ? (
            <TextField
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <>
              <Typography variant="h4">{node.data.label}</Typography>
              <IconButton
                size="small"
                sx={{ ml: 2 }}
                onClick={() => setIsEditingLabel(true)}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Row>
        {children}
        <Row sx={{ mt: 3, px: { xs: 2, sm: 3 }, justifyContent: "flex-end" }}>
          <Button variant="outlined" color="inherit" onClick={handleClosed}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!tileChanged}
            sx={{ ml: 1 }}
            onClick={handleChangesSaved}
          >
            Save
          </Button>
        </Row>
      </Column>
      <CloseUnsavedTileEditorWarningDialog
        open={unsavedTileDialogOpen}
        onClose={handleUnsavedTileDialogClosed}
      />
    </Container>
  )
}

export function useEditorDrawerState(initialFieldState2) {
  const [initialFieldState, setInitialFieldState] = useState(initialFieldState2)
  const [newFieldState, _setNewFieldState] = useState(initialFieldState2)
  const [tileChanged, setTileChanged] = useState(false)

  function setNewFieldState(newState, changeTile = true) {
    _setNewFieldState(newState)
    if (changeTile) {
      setTileChanged(true)
    }
  }

  function saveState() {
    setInitialFieldState(newFieldState)
    setTileChanged(false)
  }

  function revertState() {
    _setNewFieldState(initialFieldState)
    setTileChanged(false)
  }

  return {
    newFieldState,
    setNewFieldState,
    tileChanged,
    saveState,
    revertState,
  }
}
